import React, { Component } from 'react';
import isUndefined from 'lodash/isUndefined';
// eslint-disable-next-line @atlaskit/design-system/no-unsupported-drag-and-drop-libraries
import { type DraggableChildrenFn, Draggable } from 'react-beautiful-dnd';

import {
	type DraggableColumnProps,
	type BoardScrollPosition,
	DRAGGABLE_COLUMN_SELECTOR_NAME,
	CARD_DND_TYPE,
	ColumnTheme,
	getColumnDescriptor,
	useBoardScrollApi,
	StyledColumnWrapper,
	StyledDraggableColumn,
} from '@atlassian/jira-platform-board-kit';

import ColumnInner from './column';

type Props = DraggableColumnProps & {
	getBoardScroll: () => BoardScrollPosition;
	renderClone?: DraggableChildrenFn;
};

// eslint-disable-next-line jira/react/no-class-components
class DraggableColumnOld extends Component<Props> {
	static defaultProps = {
		isDraggable: false,
		isDropDisabled: false,
		isUpdating: false,
		isCollapsed: false,
		appearance: ColumnTheme.Default,
		draggableIndex: null,
		footer: null,
		childrenDropZoneType: CARD_DND_TYPE,
		dropZoneMode: 'standard',
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	calculateStyles(styles: any) {
		const { scrollTop } = this.props.getBoardScroll();
		if (!isUndefined(styles.top) && scrollTop > 0) {
			const top = styles.top + scrollTop;

			return {
				...styles,
				top,
			};
		}

		return styles;
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	renderColumn(isDragging: boolean, dragHandleProps: any) {
		const {
			id,
			isDraggable,
			isDropDisabled,
			isOutlined,
			isUpdating,
			swimlaneId,
			childrenDropZoneType,
			dropZoneMode,
			TransitionZones,
			renderClone,
			isCollapsed,
			isFlexible,
		} = this.props;

		return (
			<ColumnInner
				id={id}
				swimlaneId={swimlaneId}
				isDraggable={isDraggable}
				isDragging={isDragging}
				isDropDisabled={isDropDisabled}
				isOutlined={isOutlined}
				isUpdating={isUpdating}
				akDragHandleProps={dragHandleProps}
				appearance={this.props.appearance}
				header={this.props.header}
				footer={this.props.footer}
				childrenDropZoneType={childrenDropZoneType}
				TransitionZones={TransitionZones}
				dropZoneMode={dropZoneMode}
				renderClone={renderClone}
				isCollapsed={isCollapsed}
				isFlexible={isFlexible}
			>
				{this.props.children}
			</ColumnInner>
		);
	}

	render() {
		const { isDraggable, draggableIndex, header, isCollapsed, isFlexible, isDragging } = this.props;

		if (!isDraggable || !header) {
			return (
				<StyledColumnWrapper
					isCollapsed={isCollapsed}
					isFlexible={isFlexible}
					data-component-selector={DRAGGABLE_COLUMN_SELECTOR_NAME}
					data-testid="platform-board-kit-legacy-dnd.ui.column.draggable-column.styled-wrapper"
				>
					<StyledDraggableColumn
						isCollapsed={isCollapsed}
						isFlexible={isFlexible}
						isDragging={isDragging}
					>
						{this.renderColumn(false, {})}
					</StyledDraggableColumn>
				</StyledColumnWrapper>
			);
		}

		if (draggableIndex == null) {
			throw new Error('isDraggable=true => draggableIndex prop cannot be empty');
		}

		return (
			<Draggable
				draggableId={getColumnDescriptor(this.props.id, this.props.swimlaneId)}
				index={draggableIndex}
			>
				{(provided, snapshot) => (
					<StyledColumnWrapper
						data-component-selector={DRAGGABLE_COLUMN_SELECTOR_NAME}
						data-testid="platform-board-kit-legacy-dnd.ui.column.draggable-column.styled-wrapper"
						innerRef={provided.innerRef}
						{...provided.draggableProps}
						isCollapsed={isCollapsed}
						isFlexible={isFlexible}
						// eslint-disable-next-line jira/react/no-style-attribute, @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
						style={this.calculateStyles(provided.draggableProps.style)}
					>
						<StyledDraggableColumn
							isDragging={snapshot.isDragging}
							isCollapsed={isCollapsed}
							isFlexible={isFlexible}
						>
							{this.renderColumn(snapshot.isDragging, provided.dragHandleProps)}
						</StyledDraggableColumn>
					</StyledColumnWrapper>
				)}
			</Draggable>
		);
	}
}

const DraggableColumnMain = ({ ...props }: DraggableColumnProps) => {
	const [, { getBoardScroll }] = useBoardScrollApi();
	return <DraggableColumnOld getBoardScroll={getBoardScroll} {...props} />;
};

export default DraggableColumnMain;
